import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import {
  getRealmRoles,
  getResourceRoles,
  getUserEmail,
  getUsername,
} from '@/composables/useAuth'

// Keycloak's built-in realm roles; never meaningful as an operator role label.
const KC_BUILTIN_ROLES = new Set(['offline_access', 'uma_authorization'])

// Identity/roles are read from the parsed Keycloak access token (the keycloak-js
// singleton stays in useAuth.ts, not Pinia). isAdmin is a membership check over
// realm_access.roles — never roles[0]: Keycloak orders that array arbitrarily
// (composites like default-roles-master often come first), and the live master
// admin role is lowercase 'admin' while the mock token carries 'ADMIN'.
// These are cosmetic only — the real authorization boundary is server-side (TAL-570).
export const useAuthStore = defineStore('auth', () => {
  const username = ref('')
  const email = ref('')
  const realmRoles = ref<string[]>([])
  const provisioningRoles = ref<string[]>([])

  const isAdmin = computed(() =>
    realmRoles.value.some((role) => role.toLowerCase() === 'admin'),
  )
  const isProvisioningAdmin = computed(() => provisioningRoles.value.length > 0)

  // Display label for the header chip: the admin role when held, otherwise the
  // first role that isn't Keycloak plumbing (default-roles-*, offline_access, …).
  const operatorRole = computed<string | null>(() => {
    const admin = realmRoles.value.find((role) => role.toLowerCase() === 'admin')
    if (admin) return admin
    return (
      realmRoles.value.find(
        (role) => !role.startsWith('default-roles-') && !KC_BUILTIN_ROLES.has(role),
      ) ?? null
    )
  })

  function hydrate(): void {
    username.value = getUsername()
    email.value = getUserEmail()
    realmRoles.value = getRealmRoles()
    provisioningRoles.value = getResourceRoles('provisioningservice')
  }

  return {
    username,
    email,
    realmRoles,
    operatorRole,
    isAdmin,
    isProvisioningAdmin,
    hydrate,
  }
})
